<template>
  <div
    class="bedrooms-form-wrapper"
    aria-live="polite"
  >
    <div v-if="this.activePromotion.id" class="bedrooms-form-promotions-wrapper">
      <Pill
        :label="$t('STAY_BROWSE_ROOMS_PROMO_APPLIED_LABEL', { promo: activePromotion.name })"
        @remove="clearActivePromotion"
      />
    </div>
    <form
      class="bedrooms-form"
      aria-label="bedroom search form"
      tabindex="0"
      v-on-clickaway="dismissMenu"
      @submit.prevent
    >
      <BedroomsLocationMenu
        v-if="!Boolean(venueToRestrictTo)"
        :locations="locations"
        :isOpen="isLocationMenuOpen"
        :selectedLocation="location"
        :userIsMember="userIsMember"
        :promotionLocations="venuesInPromotion"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleLocationMenu"
        @itemSelected="handleLocationSelection"
      />

      <BedroomsDatesMenu
        :selectedDates="datesLabel"
        :isOpen="isDatesMenuOpen"
        :isFirst="Boolean(venueToRestrictTo)"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleDatesMenu"
        @datesSelected="handleDateSelection"
      />

      <BedroomsGuestsMenu
        :selectedGuests="guests"
        :isOpen="isGuestsMenuOpen"
        class="bedrooms-form__menu"
        @menuToggle="handleToggleGuestsMenu"
        @change="handleGuestSelection"
        :isLast="!hasBookingForMenu"
      />

      <BedroomsBookingForMenu
        v-if="hasBookingForMenu"
        :bookingTypes="availableBookingForOptions"
        :selectedBookingType="bookingFor"
        :isOpen="isBookingForMenuOpen"
        class="bedrooms-form__menu"
        @itemSelected="handleBookingTypeSelection"
        @menuToggle="handleToggleBookingForMenu"
      />

      <div class="bedrooms-form__button-wrapper">
        <ShButton
          :disabled="!canSubmit"
          :aria-disabled="!canSubmit"
          :fullWidth="!$device.isDesktop"
          @click="onSubmit"
        >
          {{ $t("STAY_BROWSE_ROOMS_HEADER") }}
        </ShButton>
      </div>
    </form>

    <div>
      <ConfirmationModal
        :name="BEDROOM_FORM_MODALS.REDIRECT"
        :title="$t('STAY_NO_BOOKING_HEADER')"
        :cancel="$t('STAY_NO_BOOKING_CLOSE_CTA')"
        :confirm="$t('STAY_NO_BOOKING_PUBLIC_CTA')"
        :to="roomBookingUrl"
        @cancel="onRedirectCancel"
      >
        {{ $t('STAY_NO_BOOKING_SUPPORTING') }}
      </ConfirmationModal>

      <ConfirmationModal
        :name="BEDROOM_FORM_MODALS.ENQUIRY"
        :title="$t('STAY_VETTED_BOOKING_HEADER')"
        :cancel="$t('STAY_VETTED_BOOKING_CLOSE_CTA')"
        :confirm="$t('STAY_VETTED_BOOKING_CTA')"
        @confirm="onEnquiryConfirm"
        @cancel="onEnquiryCancel"
      >
        {{ $t('STAY_VETTED_BOOKING_SUPPORTING') }}
      </ConfirmationModal>

      <Modal :name="BEDROOM_FORM_MODALS.FRIENDS_FAMILY">
        <div class="bedrooms-form__member-required-modal-text">
          {{
            $t('STAY_MEMBER_REQUIRED_TEXT',
            { location: location.name })
          }}
        </div>
        <template #footer>
          <ShButton @click="$ui.modal.close('friends-family-modal')">
            {{ $t('STAY_MEMBER_REQUIRED_CTA')}}
          </ShButton>
        </template>
      </Modal>
    </div>
    <div
      v-if="venueSpecificMessage"
      class="bedrooms-form-message"
      aria-live="polite"
    >
      <div class="bedrooms-form-message__main-text">
        <p class="bedrooms-form-message__icon">
          <ShIcon
            name="info"
            size="xsmall"
          />
        </p>
        <p tabindex="0">
          {{ $t(venueSpecificMessage.warningText) }}
        </p>
      </div>
      <p
        class="bedrooms-form-message__supporting-text"
        tabindex="0"
      >
        {{ $t(venueSpecificMessage.supportingText) }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  MenuLabel,
  Pill,
  ShButton,
  ShIcon
} from '@sohohouse/design-system'
import { decorators } from '@sohohouse/platform-resources'
import { mixin as clickaway } from 'vue-clickaway'
import { createNamespacedHelpers, mapGetters } from 'vuex'
import BedroomsLocationMenu from '~/components/sitecore/bedroom-search-form/menus/locations'
import BedroomsDatesMenu from '~/components/sitecore/bedroom-search-form/menus/dates'
import BedroomsGuestsMenu from '~/components/sitecore/bedroom-search-form/menus/guests'
import BedroomsBookingForMenu from '~/components/sitecore/bedroom-search-form/menus/bookingFor'
import ConfirmationModal from '~/components/modal/confirmation'
import Modal from '~/components/modal/standard'
import { getBedroomVenuesByRegion, generateSynxisBookingURL } from '~/helpers'
import { isStringTruthy } from '~/utils/environment'
import UIMixin from '~/mixins/ui'
import { FEATURES } from '~/constants/features'
import get from '~/helpers/get'

import { bookingForMenu, bookingConstants } from './menuConfig'
import bedroomsFormMixin from '~/mixins/bedrooms/bedroomsForm'

const { shared: { DateTime } } = decorators

const { mapGetters: mapMembershipGetters } = createNamespacedHelpers('user')
const { mapGetters: mapVenueGetters } = createNamespacedHelpers('venues')
const { mapActions: mapPromotionActions, mapGetters: mapPromotionGetters } = createNamespacedHelpers('promotions')

const MEMBER_REQUIRED_HOUSES = ['FARM']

const venuePushMessages = {
  SD: {
    warningText: 'STAY_PUSH_REDCHURCH_SHOREDITCH_WARNING',
    supportingText: 'STAY_PUSH_REDCHURCH_SHOREDITCH_SUPPORTING'
  },
  KET: {
    warningText: 'STAY_PUSH_REDCHURCH_KETTNERS_WARNING',
    supportingText: 'STAY_PUSH_REDCHURCH_KETTNERS_SUPPORTING'
  }
}

export const BEDROOM_FORM_MODALS = {
  FRIENDS_FAMILY: 'friends-family-modal',
  REDIRECT: 'redirect-modal',
  ENQUIRY: 'enquiry-modal'
}

export default {
  name: 'BedroomSearchForm',

  components: {
    Pill,
    MenuLabel,
    ShButton,
    BedroomsLocationMenu,
    BedroomsDatesMenu,
    BedroomsGuestsMenu,
    BedroomsBookingForMenu,
    ShIcon,
    ConfirmationModal,
    Modal
  },

  mixins: [
    UIMixin,
    clickaway,
    bedroomsFormMixin
  ],

  props: {
    emitSearch: {
      type: Boolean,
      default: false
    },
    // Location code if the form should be
    // restricted to search
    // only one venue (e.g. for modifying)
    venueToRestrictTo: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      BEDROOM_FORM_MODALS
    }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapMembershipGetters(['membershipType']),
    ...mapVenueGetters(['venuesWithRooms']),
    ...mapPromotionGetters({ promotionById: 'resourceById', promotions: 'resources' }),

    roomBookingUrl () {
      // This is because new URL in generateSynxisBookingURL isn't available on server side
      if (this.$isServer) return ''
      const { location, dates, guests, bookingFor } = this

      const { hotel: { roomBookingUrl } = {} } = this.location
      return roomBookingUrl ? generateSynxisBookingURL(roomBookingUrl, {
        location,
        dates,
        guests,
        bookingFor
      }) : ''
    },

    locations () {
      return getBedroomVenuesByRegion(this.venuesWithRooms)
    },

    canSubmit () {
      const {
        location: {
          id: locationId
        },
        dates: {
          fromDate,
          toDate
        },
        guests: {
          adults
        },
        bookingFor: {
          code: bookingForCode
        }
      } = this

      if (this.userIsMember) {
        return Boolean(
          locationId && adults && fromDate && toDate && bookingForCode
        )
      }

      return Boolean(
        locationId && adults && fromDate && toDate
      )
    },

    availableBookingForOptions () {
      const {
        hotel: { friendsAndFamily: isFriendsAndFamilyBookingEnabled } = {}
      } = this.location
      return bookingForMenu(isFriendsAndFamilyBookingEnabled)
    },

    userIsMember () {
      return Boolean(this.membershipType)
    },

    datesLabel () {
      const { fromDate, toDate } = this.dates
      // Don't show the label
      // unless both are in the query
      if (!fromDate || !toDate || fromDate === toDate) return ''
      return `${new DateTime(fromDate).dateObj.toFormat('dd LLL')} - ${new DateTime(toDate).dateObj.toFormat('dd LLL yyyy')}`
    },

    location () {
      const location = this.venueToRestrictTo || this.$route.query.location
      if (!location) return {}

      // Guard against location not in promo
      // being selected via query string
      if (this.activePromotion.id && !this.venuesInPromotion.includes(location)) return {}

      const selectedVenue = this.venuesWithRooms.find(venue => venue.id === location) || {
        code: ''
      }

      // Guard against members-only location
      // being selected by guest
      // via query string
      if (
        !this.currentUser
        && get(selectedVenue, 'hotel.publicAccess') === bookingConstants.MEMBERS_ONLY_HOUSE_VALUE
      ) {
        return {}
      }
      return selectedVenue
    },

    dates () {
      const { toDate = '', fromDate = '' } = this.$route.query
      return {
        fromDate,
        toDate
      }
    },

    guests () {
      const { adults, children } = this.$route.query

      // Return "default" value if
      // neither is set in URL
      if (!adults && !children) {
        return {
          adults: 2,
          children: 0
        }
      }

      return {
        adults: adults ? Number(adults) : 2,
        children: children ? Number(children) : 0
      }
    },

    bookingFor () {
      const { bookingForOthers } = this.$route.query
      if (!this.userIsMember || bookingForOthers === undefined) return {}

      const { FRIENDS_AND_FAMILY_VALUE, MYSELF_VALUE } = bookingConstants
      return this.availableBookingForOptions.find(({ value }) => isStringTruthy(bookingForOthers)
        ? value === FRIENDS_AND_FAMILY_VALUE
        : value === MYSELF_VALUE)
    },

    activePromotion () {
      const { promo } = this.$route.query
      // Only get active promotion
      // if promotions have been loaded
      // in the store
      if (this.promotions && this.promotions.length) {
        return this.promotionById(promo) || {}
      }
      return {}
    },

    venuesInPromotion () {
      if (!this.activePromotion || !this.activePromotion.hotels) return []
      return this.activePromotion.hotels.map(hotel => hotel.id)
    },

    venueSpecificMessage () {
      if (venuePushMessages[this.$route.query.location]) {
        return venuePushMessages[this.$route.query.location]
      }

      return ''
    },

    hasBookingForMenu () {
      if (!this.userIsMember) return false
      return !this.venueToRestrictTo
    },

    showFriendsAndFamilyModal () {
      return this.$route.query.bookingForOthers
        && MEMBER_REQUIRED_HOUSES.includes(this.location.id)
        && !this.$route.path.includes('modify')
    },

    searchFeatureEnabled () {
      return this.$isFeatureEnabled(FEATURES.roomBooking) && this.location.hotel.owsEnabled
    },

    isVettedHouse () {
      return !this.userIsMember
          && this.location.hotel.publicAccess === bookingConstants.VETTED_HOUSE_VALUE
    }
  },

  watch: {
    '$route.query.bookingForOthers': {
      handler: 'handleFriendsFamilyModal'
    }
  },

  mounted () {
    // TODO: Extend this logic to
    // public users when promotions API
    // is available to public users.
    // This request should also be moved
    // to the fetch method, though
    // it currently doesn't seem to work correctly.
    if (this.userIsMember && this.$route.query.promo) {
      this.requestRoomPromotions()
    }

    if (this.emitSearch) {
      this.onSubmit()
    }
  },

  beforeDestroy () {
    // Prevents modal from being left open if user
    // navigates to and from with browser navigation
    this.closeAllModals()
  },

  methods: {
    ...mapPromotionActions(['fetchPromotions']),

    requestRoomPromotions () {
      // Skips if promotions are in store already
      if (!this.promotions || !this.promotions.length) {
        this.fetchPromotions()
      }
    },

    clearActivePromotion () {
      this.updateQueryString({
        promo: undefined
      })
    },

    handleLocationSelection (location) {
      if (location.id === this.$route.query.location) return

      this.updateQueryString({
        location: location.id,
        bookingForOthers: undefined
      })
      if (!this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    handleBookingTypeSelection (selection) {
      const bookingForOthers = selection.value === bookingConstants.FRIENDS_AND_FAMILY_VALUE

      if (bookingForOthers === this.$route.query.bookingForOthers) return

      this.updateQueryString({ bookingForOthers })
      if (!this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    handleGuestSelection (selection) {
      this.updateQueryString(selection)
    },

    handleDateSelection (dates) {
      this.updateQueryString({
        fromDate: dates.fromDate,
        toDate: dates.toDate
      })

      if (dates.toDate !== dates.fromDate && !this.$device.isMobile) {
        this.dismissMenu()
      }
    },

    updateQueryString (query) {
      const newQuery = {
        ...this.$route.query,
        ...query
      }

      // Guard against duplicate navigation
      if (JSON.stringify(this.$route.query) === JSON.stringify(newQuery)) return

      this.$router.replace({
        path: this.$route.path,
        query: newQuery
      })
    },

    onRedirectCancel () {
      this.$ui.modal.close(BEDROOM_FORM_MODALS.REDIRECT)
      this.pushToBedroomsHome()
    },

    onEnquiryConfirm () {
      this.$ui.modal.close(BEDROOM_FORM_MODALS.ENQUIRY)
      this.$router.push(this.localePath({ name: 'contact', query: this.generateEnquiryQuery() }))
    },

    onEnquiryCancel () {
      this.$ui.modal.close('enquiry-modal')
      this.pushToBedroomsHome()
    },

    handleFriendsFamilyModal () {
      // Restrict the logic to
      // the main bedrooms landing page
      // and not the RLP.
      // This prevents the modal from
      // reappearing when submitting the search.
      // May need to be changed based on product requirements.
      if (this.showFriendsAndFamilyModal) {
        this.$ui.modal.open(BEDROOM_FORM_MODALS.FRIENDS_FAMILY)
      }
    },

    generateEnquiryQuery () {
      return {
        type: 'bedrooms_enquiry',
        subType: 'enquiry',
        venue: this.location.name,
        textId: `${this.$t('STAY_VETTED_BOOKING_ENQUIRY_TEXT', { dates: this.datesLabel })}`
      }
    },

    onSubmit () {
      if (this.emitSearch) {
        this.dismissMenu()
        return this.$emit('search', this.searchFeatureEnabled, this.isVettedHouse)
      }

      if (this.searchFeatureEnabled) {
        return this.$router.push(this.localePath({
          name: 'bedrooms-search',
          query: this.$route.query
        }))
      }

      if (this.isVettedHouse) {
        this.$ui.modal.open(BEDROOM_FORM_MODALS.ENQUIRY)
      }
      return this.$ui.modal.open(BEDROOM_FORM_MODALS.REDIRECT)
    },

    closeAllModals () {
      this.$ui.modal.close(BEDROOM_FORM_MODALS.FRIENDS_FAMILY)
      this.$ui.modal.close(BEDROOM_FORM_MODALS.REDIRECT)
      this.$ui.modal.close(BEDROOM_FORM_MODALS.ENQUIRY)
    },

    pushToBedroomsHome () {
      // Placeholder is the name of the sitecore page
      if (this.$parent.$options.name !== 'Placeholder') {
        this.$router.push(this.localePath({
          path: '/bedrooms',
          query: this.$route.query
        }))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$bedroom-form-tablet-width: 425px;
$bedroom-form-max-width: 1440px;
$bedroom-menu-max-width: 400px;
$textbox-max-width: 900px;

.bedrooms-form-wrapper {
  background-color: $color-white;
  margin-bottom: $space-070;
  padding: 0 $space-050;

  @include desktop {
    // Negative margin to
    // prevent extra space from being there
    // until the form is in sticky position
    margin: -$space-060 0;
    position: sticky;
    top: 0;
    z-index: calc(#{$z-index-navbar} - 1);
    padding: $space-060;
  }
}

.bedrooms-form {
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;

  @include tablet {
    max-width: $bedroom-form-tablet-width;
    margin: 0 auto;
  }

  @include desktop {
    flex-direction: row;
    justify-content: flex-start;
    margin: initial;
    max-width: $bedroom-form-max-width;
  }

  &__menu {
    width: 100%;
    position: relative;

    @include desktop {
      max-width: $bedroom-menu-max-width;

      // Ensures label and menu line up
      &:not(:last-child) {
        margin-right: -1px;
      }
    }
  }

  &__button-wrapper {
    margin-top: $space-080;
    width: 100%;

    @include desktop {
      margin-top: 0;
      margin-left: $space-040;
      width: initial;
    }
  }

  &__member-required-modal-text {
    text-align: center;
  }
}

.bedrooms-form-promotions-wrapper {
  padding: $space-050 0;

  @include tablet {
    max-width: $bedroom-form-tablet-width;
    margin: 0 auto;
  }

  @include desktop {
    max-width: unset;
    margin: 0;
  }
}

.bedrooms-form-message {
  margin-top: $space-060;

  &__icon {
    padding: 0 $space-030;
    display: flex;
    align-items: center;
  }

  &__main-text {
    display: flex;
    margin-bottom: $space-030;
  }

  &__supporting-text {
    @include tablet {
      max-width: $textbox-max-width;
    }
  }
}
</style>
